import type { DocsSearchEntry, DocsSearchResult } from './docsTypes'
import { searchLocationToPath } from './docsPaths'

const SNIPPET_LENGTH = 140
const TITLE_MATCH_SCORE = 10
const TEXT_MATCH_SCORE = 1

export type SearchSnippetPart = {
  text: string
  highlight: boolean
}

function tokenizeSearchQuery(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean)
}

function stripSearchText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function buildSearchSnippet(plainText: string, query: string, maxLength = SNIPPET_LENGTH): string {
  if (!plainText) {
    return ''
  }

  const tokens = tokenizeSearchQuery(query)
  const lowerText = plainText.toLowerCase()

  let matchIndex = -1
  for (const token of tokens) {
    const index = lowerText.indexOf(token)
    if (index !== -1) {
      matchIndex = index
      break
    }
  }

  if (matchIndex === -1) {
    return plainText.length <= maxLength ? plainText : `${plainText.slice(0, maxLength - 1).trim()}…`
  }

  const halfWindow = Math.floor(maxLength / 2)
  let start = Math.max(0, matchIndex - halfWindow)
  let end = Math.min(plainText.length, start + maxLength)

  if (end - start < maxLength) {
    start = Math.max(0, end - maxLength)
  }

  let snippet = plainText.slice(start, end).trim()
  if (start > 0) {
    snippet = `…${snippet}`
  }
  if (end < plainText.length) {
    snippet = `${snippet}…`
  }

  return snippet
}

export function splitHighlightedSnippet(snippet: string, query: string): SearchSnippetPart[] {
  const tokens = tokenizeSearchQuery(query).sort((a, b) => b.length - a.length)
  if (tokens.length === 0 || !snippet) {
    return [{ text: snippet, highlight: false }]
  }

  const pattern = tokens.map(escapeRegExp).join('|')
  const parts: SearchSnippetPart[] = []
  let lastIndex = 0

  for (const match of snippet.matchAll(new RegExp(pattern, 'gi'))) {
    const index = match.index ?? 0
    if (index > lastIndex) {
      parts.push({ text: snippet.slice(lastIndex, index), highlight: false })
    }
    parts.push({ text: match[0], highlight: true })
    lastIndex = index + match[0].length
  }

  if (lastIndex < snippet.length) {
    parts.push({ text: snippet.slice(lastIndex), highlight: false })
  }

  return parts.length > 0 ? parts : [{ text: snippet, highlight: false }]
}

function scoreSearchEntry(entry: DocsSearchEntry, tokens: string[]): number {
  const title = entry.title.toLowerCase()
  const text = stripSearchText(entry.text).toLowerCase()
  let score = 0

  for (const token of tokens) {
    if (title.includes(token)) {
      score += TITLE_MATCH_SCORE
    }
    if (text.includes(token)) {
      score += TEXT_MATCH_SCORE
    }
  }

  return score
}

export function searchDocs(entries: DocsSearchEntry[], query: string, limit = 8): DocsSearchResult[] {
  const tokens = tokenizeSearchQuery(query)
  if (tokens.length === 0) {
    return []
  }

  const scored = entries
    .map((entry) => ({ entry, score: scoreSearchEntry(entry, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)

  const seenLocations = new Set<string>()
  const results: DocsSearchResult[] = []

  for (const { entry } of scored) {
    if (seenLocations.has(entry.location)) {
      continue
    }
    seenLocations.add(entry.location)

    const plainText = stripSearchText(entry.text)

    results.push({
      title: entry.title,
      path: searchLocationToPath(entry.location),
      location: entry.location,
      snippet: buildSearchSnippet(plainText, query),
    })

    if (results.length >= limit) {
      break
    }
  }

  return results
}
